/**
 * CreatorEarningsModal
 * Full dashboard modal for creator earnings — stats, payout history, follower details
 */

import { useCreatorEarnings } from '@hooks/useCreatorEarnings';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { CreatorStatsGrid } from './CreatorStatsGrid';
import { FollowerPreviewTable } from './FollowerPreviewTable';
import { PayoutHistoryTable } from './PayoutHistoryTable';
import { PeriodDetailView } from './PeriodDetailView';

interface CreatorEarningsModalProps {
  onClose: () => void;
}

type DashboardView = 'overview' | 'period-detail';

export function CreatorEarningsModal({ onClose }: CreatorEarningsModalProps): ReactElement {
  const [activeView, setActiveView] = useState<DashboardView>('overview');
  const [, setSelectedPeriod] = useState<string | null>(null);

  const {
    status,
    statusLoading,
    fetchStatus,
    earnings,
    earningsLoading,
    earningsHasMore,
    fetchEarnings,
    loadMoreEarnings,
    periodDetail,
    periodDetailLoading,
    fetchPeriodDetail,
    clearPeriodDetail,
    followerPreview,
    followerPreviewLoading,
    fetchFollowerPreview,
  } = useCreatorEarnings();

  // Fetch initial data on mount
  useEffect(() => {
    fetchStatus();
    fetchEarnings();
    fetchFollowerPreview();
  }, [fetchStatus, fetchEarnings, fetchFollowerPreview]);

  const handleSelectPeriod = useCallback(
    (periodLabel: string) => {
      setSelectedPeriod(periodLabel);
      setActiveView('period-detail');
      fetchPeriodDetail(periodLabel);
    },
    [fetchPeriodDetail],
  );

  const handleBack = useCallback(() => {
    setActiveView('overview');
    setSelectedPeriod(null);
    clearPeriodDetail();
  }, [clearPeriodDetail]);

  const isLoading = statusLoading && !status;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={cn(
          'relative w-full max-w-4xl max-h-[90dvh] overflow-hidden border',
          'bg-theme-bg/95 backdrop-blur-sm rounded-2xl border-theme-border shadow-2xl shadow-black/40 mx-4',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme-border">
          <h2 className="text-xl font-semibold text-theme-text">Creator Earnings</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-theme-surface rounded-lg transition-colors"
            aria-label="Close earnings dashboard"
          >
            <X className="w-5 h-5 text-theme-text-tertiary" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90dvh-130px)]">
          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-24 bg-theme-border rounded-xl" />
                ))}
              </div>
              <div className="h-48 bg-theme-border rounded" />
            </div>
          ) : activeView === 'overview' ? (
            <div className="space-y-6">
              {/* Stats Grid */}
              {status && (
                <CreatorStatsGrid
                  status={status}
                  earnings={earnings}
                  followerPreview={followerPreview}
                />
              )}

              {/* Payout History */}
              <PayoutHistoryTable
                payouts={earnings?.payouts ?? []}
                loading={earningsLoading}
                hasMore={earningsHasMore}
                onSelectPeriod={handleSelectPeriod}
                onLoadMore={loadMoreEarnings}
              />

              {/* Follower Preview */}
              {followerPreview && (
                <FollowerPreviewTable
                  followerPreview={followerPreview}
                  loading={followerPreviewLoading}
                />
              )}
            </div>
          ) : periodDetail ? (
            <PeriodDetailView
              periodDetail={periodDetail}
              loading={periodDetailLoading}
              onBack={handleBack}
            />
          ) : (
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-theme-border rounded w-48" />
              <div className="h-48 bg-theme-border rounded" />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
